import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV, DONATE_URL } from "./data";

export const scrollToId = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled ? "bg-[#FAF7F2]/85 backdrop-blur-xl shadow-[0_1px_0_rgba(15,29,47,0.08)] py-2" : "py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center">
          <img src="/images/logo.png" alt="Wings of Fire Foundation" className="h-12 sm:h-14 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.href}
              to={n.href}
              data-testid={`nav-link-${n.href.slice(1)}`}
              className={({ isActive }) => `nav-link text-sm font-medium transition-colors hover:text-[#CF5C0F] ${isActive ? "text-[#CF5C0F]" : "text-[#48071C]"}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to={DONATE_URL} data-testid="nav-donate-button" className="btn-fire hidden sm:inline-flex">
            Donate Now <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden w-11 h-11 rounded-full border border-[#0F1D2F]/15 flex items-center justify-center text-[#0F1D2F] bg-white/70"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="lg:hidden mx-4 mt-3 rounded-3xl bg-[#FAF7F2]/95 backdrop-blur-xl border border-[#0F1D2F]/10 p-6 flex flex-col gap-4"
          >
            {NAV.map((n) => (
              <Link key={n.href} to={n.href} data-testid={`mobile-nav-${n.href.slice(1)}`} className="font-serif text-3xl text-[#0F1D2F]">
                {n.label}
              </Link>
            ))}
            <Link to={DONATE_URL} data-testid="mobile-donate-button" className="btn-fire justify-center mt-2">
              Donate Now <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
